import React from 'react';
import { DividerVariant, PaddingSize, SpacingSize } from './dividerTypes';

export type DividerLineStyle = 'solid' | 'dashed';

export type DividerOrientation = 'horizontal' | 'vertical';

/** Fixed lengths a rule between inline controls can take. Left unset it spans its container. */
export type DividerLength = 'small' | 'medium' | 'large';

const LENGTH_PX: Record<DividerLength, number> = { small: 16, medium: 24, large: 32 };

interface DividerProps {
    /** `vertical` draws a 1px rule between inline controls, e.g. groups in a toolbar. */
    orientation?: DividerOrientation;
    /** Length along the rule's own axis — height when vertical, width when horizontal.
     *  Unset spans the container. */
    length?: DividerLength;
    margin?: SpacingSize;
    padding?: PaddingSize;
    className?: string;
    variant?: DividerVariant;
    lineStyle?: DividerLineStyle;
    grow?: boolean;
}

/**
 * A 1px divider using the theme's card border. Horizontal and full-width by default;
 * `orientation="vertical"` draws a rule between inline controls instead.
 */

const getSpacingValue = (spacing?: SpacingSize): string | undefined => {
    return spacing ? `${getSpacingSize(spacing)}px` : undefined;
};

const getPaddingValue = (padding?: PaddingSize): string | undefined => {
    return padding ? `${getPaddingSize(padding)}px` : undefined;
};

const getSpacingSize = (size: SpacingSize): number => {
    switch (size) {
        case SpacingSize.XSmall:
            return 8;
        case SpacingSize.Small:
            return 12;
        case SpacingSize.Medium:
            return 24;
        case SpacingSize.Large:
            return 36;
    }
};

const getPaddingSize = (size: PaddingSize): number => {
    switch (size) {
        case PaddingSize.XSmall:
            return 1;
        case PaddingSize.Small:
            return 2;
        case PaddingSize.Medium:
            return 4;
        case PaddingSize.Large:
            return 8;
    }
};

const Divider: React.FC<DividerProps> = ({
    orientation = 'horizontal',
    length,
    margin,
    padding,
    className = '',
    variant = DividerVariant.Default,
    lineStyle = 'solid',
    grow = false,
}) => {
    const lineColor =
        variant === DividerVariant.Default
            ? 'var(--malachite-card-border)'
            : variant === DividerVariant.Muted
              ? 'var(--malachite-border-muted)'
              : 'var(--malachite-border-strong)';

    const isVertical = orientation === 'vertical';
    const dashDirection = isVertical ? 'to bottom' : 'to right';
    const span = length ? `${LENGTH_PX[length]}px` : '100%';

    const dividerStyles: React.CSSProperties = {
        height: isVertical ? span : '1px',
        width: isVertical ? '1px' : span,
        margin: margin ? getSpacingValue(margin) : undefined,
        padding: padding ? getPaddingValue(padding) : undefined,
        // Longhands, never the `flex` shorthand: it resets `flex-shrink` and a 1px rule that
        // may shrink disappears between wider siblings.
        flexGrow: grow ? 1 : undefined,
        flexBasis: grow ? '0%' : undefined,
        flexShrink: 0,
        border: 'none',
        ...(lineStyle === 'dashed'
            ? {
                  backgroundColor: 'transparent',
                  backgroundImage: `repeating-linear-gradient(${dashDirection}, ${lineColor} 0 5px, transparent 5px 11px)`,
                  backgroundSize: isVertical ? '1px 100%' : '100% 1px',
                  backgroundRepeat: 'no-repeat',
              }
            : { backgroundColor: lineColor }),
    };

    return <div className={className} style={dividerStyles} />;
};

export default Divider;
export { Divider };
